import React from "react";
import SEO from "../components/SEO";
import { Link } from "react-router-dom";
import { Clock, Navigation, ArrowUpRight } from "lucide-react";
import { ACTIVE_LOCATIONS, BRAND } from "../lib/constants";

export default function LocationsPage() {
  return (
    <>
      <SEO
        title="Visit Our DC Dispensary · 4915 Georgia Ave NW, 20011 | District Wellness Center"
        description="Visit DWC at 4915 Georgia Ave NW, Washington, DC 20011 — Petworth's neighborhood dispensary. Directions, hours, contact & DC ABCA license details."
        path="/locations"
        breadcrumbs={[{ name: "Locations", path: "/locations" }]}
      />
      <section className="bg-white border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 py-20 sm:py-28 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-5">Dispensary Information</div>
            <h1 className="font-serif text-5xl sm:text-7xl leading-[0.95] tracking-tight">
              Visit our <em className="text-brand">flagship</em> on Georgia Ave.
            </h1>
          </div>
          <div className="lg:col-span-4 text-ink-muted">
            <p>4915 Georgia Ave NW · Washington, DC. Licensed by DC ABCA, License {BRAND.licenseNumber}.</p>
            <a href={`mailto:${BRAND.email}`} className="mt-4 inline-block text-ink link-underline">{BRAND.email}</a>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#FAFAFA]" data-testid="locations-page">
        <div className="max-w-7xl mx-auto px-6 space-y-16">
          {ACTIVE_LOCATIONS.map((loc) => (
            <article key={loc.id} className="grid lg:grid-cols-12 gap-8 items-stretch border border-black/10 bg-white overflow-hidden" data-testid={`locations-page-${loc.id}`}>
              <div className="lg:col-span-6 p-10 sm:p-14 flex flex-col">
                <div className="flex items-center justify-between mb-8">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-ink-muted">{loc.label}</span>
                  <span className="text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 bg-brand text-white" data-testid={`locations-status-${loc.id}`}>Open Now</span>
                </div>
                <h2 className="font-serif text-5xl sm:text-6xl leading-[0.95] tracking-tight">{loc.name}</h2>
                <p className="mt-5 text-ink-muted leading-relaxed">{loc.blurb}</p>

                <div className="mt-8 pt-8 border-t border-black/10 grid grid-cols-2 gap-6 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-brand mb-2">Address</div>
                    <div className="font-serif text-xl leading-tight">{loc.street}</div>
                    <div className="text-ink-muted">{loc.city}</div>
                    {loc.phone && (
                      <a href={loc.phoneHref} className="mt-3 inline-block text-ink hover:text-brand text-sm" data-testid={`locpage-phone-${loc.id}`}>
                        ☎ {loc.phone}
                      </a>
                    )}
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-brand mb-2">Hours</div>
                    <div className="text-ink inline-flex items-center gap-2"><Clock size={14} /> {loc.hours}</div>
                  </div>
                </div>

                <div className="mt-10 flex flex-col sm:flex-row gap-3">
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc.mapQuery)}`}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-2 bg-ink hover:bg-black text-white px-6 py-4 text-[11px] uppercase tracking-[0.22em]"
                    data-testid={`page-directions-${loc.id}`}
                  >
                    <Navigation size={14} /> Get Directions
                  </a>
                  <Link
                    to="/menu"
                    className="inline-flex items-center gap-2 bg-brand hover:bg-brand-600 text-white px-6 py-4 text-[11px] uppercase tracking-[0.22em]"
                    data-testid={`page-order-${loc.id}`}
                  >
                    Order Online <ArrowUpRight size={14} />
                  </Link>
                </div>
              </div>
              <div className="lg:col-span-6 min-h-[400px] relative bg-black/5">
                <iframe
                  title={`Map for ${loc.name}`}
                  src={`https://www.google.com/maps?q=${encodeURIComponent(loc.mapQuery)}&output=embed`}
                  className="absolute inset-0 w-full h-full"
                  style={{ border: 0 }}
                  loading="lazy"
                  data-testid={`map-${loc.id}`}
                />
              </div>
            </article>
          ))}
        </div>
      </section>
    </>
  );
}
